import React, { useRef } from 'react';
import { useHRTopPerformers } from '../../hooks/useHRQueries';
import LoadingSpinner from '../ui/LoadingSpinner';
import { formatNumber } from '../../utils/formatters';
import ChartControls from '../ui/ChartControls';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface TopPersonnelPerformanceProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}

const TopPersonnelPerformance: React.FC<TopPersonnelPerformanceProps> = ({ 
  branchId,
  showLoading = true,
  onError 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useHRTopPerformers(branchId);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Component metadata
  const componentId = 'top-personnel-performance';
  const componentTitle = 'عملکرد پرسنل';

  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <LoadingSpinner variant="minimal" size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-base border border-status-error p-4">
        <p className="text-status-error">خطا در بارگذاری پرسنل برتر</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const roleNames = {
    cashier: 'صندوقداران برتر',
    courier: 'پیک‌های برتر',
    host: 'میزبان‌های برتر'
  };

  return (
    <div ref={chartRef}>
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="mixed"
          dashboardSource="hr"
          onInfo={() => openGuide(CHART_GUIDES.topPersonnelPerformance)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'hr',
            componentType: 'mixed',
            description: 'پرسنل برتر بر اساس نقش',
            props: { branchId, showLoading, onError },
            gridLayout: {
              containerClass: 'grid grid-cols-1 gap-6'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
          className="mb-4"
        />
        <h2 className="title-section">{componentTitle}</h2>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-3 gap-4">
        {['cashier', 'courier', 'host'].map((role) => {
          const topPerformers = data[role] || [];
          
          return (
            <div className="card-base card-padding" key={role}>
              <div className="bg-bg-secondary rounded-lg p-4">
                <h3 className="title-card">
                  {roleNames[role as keyof typeof roleNames]}
                </h3>
                
                <div className="space-y-3">
                  {topPerformers.map((performer: any, index: number) => (
                    <div key={index} className="flex items-center bg-bg-primary p-3 rounded-lg hover:shadow-md transition-all">
                      <div className={`w-8 h-8 ${
                        index === 0 ? 'bg-yellow-500' :
                        index === 1 ? 'bg-gray-400' :
                        'bg-orange-600'
                      } rounded-full flex items-center justify-center text-white text-xs font-bold ml-3`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-text-primary">
                          {performer.name}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-status-success font-medium">
                            {performer.satisfaction}% رضایت
                          </span>
                          <span className="text-xs text-text-secondary">
                            {formatNumber(performer.invoices)} عملیات
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {topPerformers.length === 0 && (
                    <div className="text-center text-text-tertiary text-sm py-4">
                      داده‌ای موجود نیست
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TopPersonnelPerformance;